// CMP-0108 · LFS Lot Gallery (main photo, side tiles, full-screen viewer) · Catalog Engine · Lot Discovery · owner: Platform
// 99-1: /Users/yogeshmathur/ym/lot-for-sale-ph/docs/design-handoff/README.md
// 99-1-decision: ADAPT the Property.dc.html gallery (registry SEL-LFS-001); real listing photos replace the prototype's stand-ins, with a labelled representative photo only when a lot has none
"use client"

import Image from "@/components/lfs/img"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Icon } from "./primitives"

export function LotGallery({ photos, title, badge, fallback, inquireHref, areaHref, areaName }: {
  photos: string[]
  title: string
  badge: string
  fallback: string
  inquireHref: string
  areaHref: string
  areaName: string
}) {
  const [open, setOpen] = useState<number | null>(null)
  const has = photos.length > 0
  const n = photos.length

  useEffect(() => {
    if (open === null) return
    document.body.style.overflow = "hidden"
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null)
      if (e.key === "ArrowRight") setOpen((i) => (i === null ? i : (i + 1) % n))
      if (e.key === "ArrowLeft") setOpen((i) => (i === null ? i : (i - 1 + n) % n))
    }
    window.addEventListener("keydown", onKey)
    return () => {
      document.body.style.overflow = ""
      window.removeEventListener("keydown", onKey)
    }
  }, [open, n])

  const tile = { position: "relative", borderRadius: 24, overflow: "hidden", background: "#EEF0F3", minHeight: 0 } as const

  return (
    <>
      <div className="lfs-lot-gallery" style={{ marginTop: 24, display: "grid", gap: 10 }}>
        <div style={tile}>
          {has ? (
            <Button type="button" variant="ghost" onClick={() => setOpen(0)} aria-label={`View photos of ${title}`} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", padding: 0, borderRadius: 0 }}>
              <Image src={photos[0]} alt={title} fill priority sizes="(min-width:768px) 66vw, 100vw" style={{ objectFit: "cover" }} />
            </Button>
          ) : (
            <Image src={fallback} alt={`${badge} (representative photo)`} fill priority sizes="(min-width:768px) 66vw, 100vw" style={{ objectFit: "cover" }} />
          )}
          <span style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg,rgba(0,0,0,0) 55%,rgba(0,0,0,.55))", pointerEvents: "none" }} />
          <span style={{ position: "absolute", left: 16, top: 16, height: 30, padding: "0 10px", borderRadius: 999, background: "rgba(255,255,255,.94)", display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#121722", pointerEvents: "none" }}>{badge}</span>
          <span style={{ position: "absolute", left: 16, right: 16, bottom: 14, display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "end", gap: 8, color: "#fff" }}>
            <span style={{ fontSize: 13, opacity: 0.92 }}>{has ? `Listing photo 1 of ${n}` : "Representative photo, not this lot"}</span>
            {has ? (
              n > 1 && <Button type="button" variant="ghost" onClick={() => setOpen(0)} style={{ height: 38, padding: "0 14px", borderRadius: 999, background: "#fff", color: "#121722", gap: 6, fontSize: 14, fontWeight: 600 }}><Icon name="photo_library" size={18} />View all {n} photos</Button>
            ) : (
              <a href={inquireHref} style={{ height: 38, padding: "0 14px", borderRadius: 999, background: "#fff", color: "#121722", display: "flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, textDecoration: "none" }}><Icon name="photo_library" size={18} />Request actual photos</a>
            )}
          </span>
        </div>
        <div className="lfs-lot-gallery-side" style={{ display: "grid", gridTemplateRows: "1fr 1fr", gap: 10, minHeight: 0 }}>
          {[1, 2].map((i) =>
            photos[i] ? (
              <Button key={i} type="button" variant="ghost" onClick={() => setOpen(i)} aria-label={`View photo ${i + 1} of ${n}`} style={{ ...tile, height: "100%", width: "100%", padding: 0 }}>
                <Image src={photos[i]} alt={`${title}, photo ${i + 1}`} fill sizes="33vw" style={{ objectFit: "cover" }} />
              </Button>
            ) : i === 1 ? (
              <a key={i} href={areaHref} style={{ ...tile, textDecoration: "none", color: "#fff", display: "block" }}>
                <Image src="/lfs/hero-tagaytay-road.jpg" alt="" fill sizes="33vw" style={{ objectFit: "cover" }} />
                <span style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg,rgba(0,0,0,0) 40%,rgba(0,0,0,.6))" }} />
                <span style={{ position: "absolute", left: 16, right: 16, bottom: 14, display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", opacity: 0.9 }}>Area</span>
                  <b style={{ fontSize: 18, display: "flex", alignItems: "center", gap: 4 }}>{areaName}<Icon name="arrow_forward" size={18} /></b>
                </span>
              </a>
            ) : (
              <div key={i} style={{ ...tile, background: "#F3F5F8", padding: 18, display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 10 }}>
                <span style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: "#121722", fontWeight: 700 }}>Exact location</span>
                <span style={{ fontSize: 15, lineHeight: 1.45, color: "#3B4352" }}>Pin, survey plan and more photos are shared by the broker after inquiry, with the seller’s consent.</span>
                <a href="#h-map" style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 4, textDecoration: "none" }}>See area<Icon name="south" size={18} /></a>
              </div>
            ),
          )}
        </div>
      </div>

      {open !== null && has && (
        <div role="dialog" aria-modal="true" aria-label={`Photos of ${title}`} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(10,13,20,.94)", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", color: "#fff" }}>
            <span style={{ fontSize: 14 }}>{open + 1} / {n}</span>
            <Button type="button" variant="ghost" onClick={() => setOpen(null)} aria-label="Close photos" style={{ width: 44, height: 44, padding: 0, color: "#fff" }}><Icon name="close" size={26} /></Button>
          </div>
          <div style={{ position: "relative", flex: 1, minHeight: 0, margin: "0 8px" }}>
            <Image src={photos[open]} alt={`${title}, photo ${open + 1}`} fill sizes="100vw" style={{ objectFit: "contain" }} />
          </div>
          {n > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 12, padding: "12px 16px calc(16px + env(safe-area-inset-bottom))" }}>
              <Button type="button" variant="ghost" onClick={() => setOpen((open - 1 + n) % n)} aria-label="Previous photo" style={{ width: 52, height: 52, padding: 0, borderRadius: 999, background: "rgba(255,255,255,.14)", color: "#fff" }}><Icon name="chevron_left" size={28} /></Button>
              <Button type="button" variant="ghost" onClick={() => setOpen((open + 1) % n)} aria-label="Next photo" style={{ width: 52, height: 52, padding: 0, borderRadius: 999, background: "rgba(255,255,255,.14)", color: "#fff" }}><Icon name="chevron_right" size={28} /></Button>
            </div>
          )}
        </div>
      )}
    </>
  )
}
